import React from 'react';
import { parseISO } from 'date-fns';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MatchHeader = ({ match, onShare, formatDate }) => {
  if (!match) return null;

  const matchDate = parseISO(match?.date);

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden mb-6">
      {/* Header Image/Background */}
      <div className="relative h-48 md:h-64 bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/10">
        <div className="absolute inset-0 bg-black/10" />
        
        {/* Match Status Indicator */}
        <div className="absolute top-4 left-4">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            match?.status === 'confirmed' ?'bg-success text-success-foreground' 
              : match?.status === 'pending' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground'
          }`}>
            {match?.status === 'confirmed' && (
              <>
                <Icon name="CheckCircle" size={16} className="mr-1" />
                Confirmada
              </>
            )}
            {match?.status === 'pending' && (
              <>
                <Icon name="Clock" size={16} className="mr-1" />
                Pendente
              </>
            )}
            {match?.status === 'cancelled' && (
              <>
                <Icon name="XCircle" size={16} className="mr-1" />
                Cancelada
              </>
            )}
          </span>
        </div>

        {/* Share Button */}
        <div className="absolute top-4 right-4">
          <Button
            variant="secondary"
            size="sm"
            iconName="Share2"
            onClick={onShare}
            className="bg-white/90 hover:bg-white text-foreground"
          />
        </div>

        {/* Match Title and Basic Info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-6">
          <div className="text-white">
            <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
              {match?.title}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={18} />
                <span className="font-medium">
                  {formatDate?.(match?.date)} • {match?.time}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={18} />
                <span className="font-medium">{match?.venue?.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      <div className="p-6">
        {/* Match Description */}
        {match?.description && (
          <div className="mb-6">
            <p className="text-muted-foreground leading-relaxed">
              {match?.description}
            </p>
          </div>
        )}

        {/* Key Information Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Participants */}
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-2">
              <Icon name="Users" size={20} className="text-primary" />
            </div>
            <div className="text-lg font-bold text-foreground">
              {match?.confirmedPlayers?.length || 0}
            </div>
            <div className="text-sm text-muted-foreground">
              de {match?.maxPlayers} jogadores
            </div>
          </div>

          {/* Price */}
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg mx-auto mb-2">
              <Icon name="DollarSign" size={20} className="text-success" />
            </div>
            <div className="text-lg font-bold text-foreground">
              R$ {match?.pricePerPlayer?.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">por pessoa</div>
          </div>

          {/* Duration */}
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-info/10 rounded-lg mx-auto mb-2">
              <Icon name="Clock" size={20} className="text-info" />
            </div>
            <div className="text-lg font-bold text-foreground">
              {Math.floor(match?.duration / 60)}h{match?.duration % 60 > 0 ? ` ${match?.duration % 60}m` : ''}
            </div>
            <div className="text-sm text-muted-foreground">duração</div>
          </div>

          {/* Creator Rating */}
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mx-auto mb-2">
              <Icon name="Star" size={20} className="text-warning" />
            </div>
            <div className="text-lg font-bold text-foreground">
              {match?.creator?.reputation?.toFixed(1)}
            </div>
            <div className="text-sm text-muted-foreground">organizador</div>
          </div>
        </div>

        {/* Creator Information */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              {match?.creator?.avatar ? (
                <img
                  src={match?.creator?.avatar}
                  alt={match?.creator?.avatarAlt || `Avatar de ${match?.creator?.name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={20} className="text-muted-foreground" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Organizado por {match?.creator?.name}
              </p>
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={12} className="text-warning" />
                <span className="text-xs text-muted-foreground">
                  {match?.creator?.reputation?.toFixed(1)} • Organizador confiável
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmed Players Avatars */}
        {match?.confirmedPlayers?.length > 0 && (
          <div className="border-t border-border pt-4 mt-4">
            <div className="flex items-center space-x-3">
              <div className="flex -space-x-2">
                {match?.confirmedPlayers?.slice(0, 5)?.map((player, index) => (
                  <div
                    key={player?.id}
                    className="w-8 h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden"
                  >
                    {player?.avatar ? (
                      <img
                        src={player?.avatar}
                        alt={player?.avatarAlt || `Avatar de ${player?.name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={12} className="text-muted-foreground" />
                    )}
                  </div>
                ))}
                {match?.confirmedPlayers?.length > 5 && (
                  <div className="w-8 h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center">
                    <span className="text-xs font-medium text-muted-foreground">
                      +{match?.confirmedPlayers?.length - 5}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-card-foreground">
                  {match?.confirmedPlayers?.length} jogadores confirmados
                </p>
                <p className="text-xs text-muted-foreground">
                  {match?.pendingPlayers?.length > 0 && `${match?.pendingPlayers?.length} pendentes`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchHeader;